// "Who's playing?" preset cards. Each preset has its own look: Explorer is
// bright and playful for young players, Grand Dominion sober and dark. The
// cover art is reused from the pause cards (public/assets/images/pause).
// Cards are radios in a roving-tabindex radiogroup (see SetupScreen).

import { presetById } from '@aod/protocol';
import { cn } from '@aod/ui/lib/utils';
import {
  Castle,
  Check,
  Clock,
  Compass,
  Crown,
  Flame,
  Heart,
  type LucideIcon,
  Map as MapIcon,
  Minus,
  Mountain,
  Sparkles,
  Star,
  Swords,
} from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { formatSession, presetName } from './presentation.js';
import type { PresetDefId } from './setupModel.js';

interface Look {
  readonly Icon: LucideIcon;
  readonly BulletIcon: LucideIcon;
  readonly image: string;
  /** Classes on the card itself (shape, border, hover). */
  readonly card: string;
  /** Tint laid over the cover art so text stays readable. */
  readonly overlay: string;
  readonly imageClass?: string;
  readonly iconWrap: string;
  readonly title: string;
  readonly tagline: string;
  readonly badge: string;
  readonly bullet: string;
  readonly bulletIcon: string;
}

const ART = '/assets/images/pause';

export const PRESET_LOOK: Record<PresetDefId, Look> = {
  explorer: {
    Icon: Compass,
    BulletIcon: Star,
    image: `${ART}/nature-640.webp`,
    card: 'bg-fuchsia-700 rounded-3xl border-2 border-white/40 hover:-translate-y-1 hover:-rotate-[0.6deg]',
    overlay: 'bg-gradient-to-br from-sky-400/85 via-fuchsia-500/55 to-amber-400/85',
    iconWrap: 'rounded-2xl bg-white/90 text-fuchsia-600 shadow-lg shadow-fuchsia-900/30',
    title: 'text-2xl font-black tracking-tight text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.45)]',
    tagline: 'font-semibold text-white',
    badge: 'rounded-full bg-amber-300 px-2.5 text-amber-950 ring-2 ring-white/70',
    bullet: 'font-semibold text-white',
    bulletIcon: 'fill-amber-200 text-amber-200',
  },
  apprentice: {
    Icon: MapIcon,
    BulletIcon: Check,
    image: `${ART}/realm-dawn-640.webp`,
    card: 'bg-slate-900 rounded-2xl border border-emerald-200/30 hover:-translate-y-0.5',
    overlay: 'bg-gradient-to-b from-emerald-700/35 via-emerald-950/65 to-emerald-950/95',
    iconWrap: 'rounded-xl bg-emerald-400/90 text-emerald-950',
    title: 'text-xl font-extrabold text-white',
    tagline: 'text-emerald-100',
    badge: 'rounded-full bg-emerald-300 px-2 text-emerald-950',
    bullet: 'text-emerald-50',
    bulletIcon: 'text-emerald-300',
  },
  standard: {
    Icon: Swords,
    BulletIcon: Check,
    image: `${ART}/realm-harbor-640.webp`,
    card: 'bg-slate-900 rounded-xl border border-amber-200/25 hover:-translate-y-0.5',
    overlay: 'bg-gradient-to-b from-slate-900/25 via-slate-950/70 to-slate-950/95',
    iconWrap: 'rounded-lg bg-amber-300/90 text-amber-950',
    title: 'text-xl font-bold text-white',
    tagline: 'text-amber-100/90',
    badge: 'rounded-md bg-amber-200/90 px-2 text-amber-950',
    bullet: 'text-slate-100',
    bulletIcon: 'text-amber-300',
  },
  strategist: {
    Icon: Castle,
    BulletIcon: Check,
    image: `${ART}/realm-pass-640.webp`,
    card: 'bg-slate-900 rounded-xl border border-indigo-200/25 hover:-translate-y-0.5',
    overlay: 'bg-gradient-to-b from-indigo-950/40 via-indigo-950/75 to-slate-950/95',
    iconWrap: 'rounded-lg bg-indigo-300/90 text-indigo-950',
    title: 'text-xl font-bold text-white',
    tagline: 'text-indigo-100/90',
    badge: 'rounded-md bg-indigo-200/90 px-2 text-indigo-950',
    bullet: 'text-slate-100',
    bulletIcon: 'text-indigo-300',
  },
  grand: {
    Icon: Crown,
    BulletIcon: Minus,
    image: `${ART}/science-640.webp`,
    imageClass: 'grayscale-[70%] brightness-75',
    card: 'bg-slate-900 rounded-md border border-amber-100/15',
    overlay: 'bg-gradient-to-b from-zinc-950/55 via-zinc-950/85 to-black/95',
    iconWrap: 'rounded-sm border border-amber-100/30 text-amber-100/85',
    title: 'font-serif text-lg font-semibold uppercase tracking-[0.16em] text-amber-50/95',
    tagline: 'font-serif italic text-zinc-300',
    badge: 'rounded-sm border border-amber-100/30 px-1.5 text-amber-100/85',
    bullet: 'text-zinc-300',
    bulletIcon: 'text-amber-100/50',
  },
  highlands: {
    Icon: Mountain,
    BulletIcon: Mountain,
    image: `${ART}/nature-640.webp`,
    imageClass: 'saturate-[0.8] brightness-90',
    card: 'bg-slate-900 rounded-xl border border-emerald-100/20',
    overlay: 'bg-gradient-to-b from-emerald-950/45 via-slate-950/70 to-black/90',
    iconWrap: 'rounded-lg border border-emerald-100/30 text-emerald-100/90',
    title: 'font-serif text-xl font-bold tracking-wide text-emerald-50',
    tagline: 'font-semibold text-emerald-100/90',
    badge: 'rounded-full bg-emerald-200 px-2.5 text-emerald-950 ring-2 ring-white/60',
    bullet: 'text-emerald-100/85',
    bulletIcon: 'text-emerald-200/60',
  },
  legends: {
    Icon: Flame,
    BulletIcon: Flame,
    image: `${ART}/chaos-640.webp`,
    imageClass: 'saturate-[0.9] brightness-90',
    card: 'bg-slate-900 rounded-xl border border-orange-100/20',
    overlay: 'bg-gradient-to-b from-orange-950/45 via-slate-950/70 to-black/90',
    iconWrap: 'rounded-lg border border-orange-100/30 text-orange-100/90',
    title: 'font-serif text-xl font-bold tracking-wide text-orange-50',
    tagline: 'font-semibold text-orange-100/90',
    badge: 'rounded-full bg-orange-200 px-2.5 text-orange-950 ring-2 ring-white/60',
    bullet: 'text-orange-100/85',
    bulletIcon: 'text-orange-200/60',
  },
  seafarers: {
    Icon: Compass,
    BulletIcon: Star,
    image: `${ART}/nature-640.webp`,
    imageClass: 'saturate-[1.05] brightness-95',
    card: 'bg-sky-950 rounded-xl border border-sky-100/25',
    overlay: 'bg-gradient-to-b from-sky-900/45 via-sky-950/70 to-black/90',
    iconWrap: 'rounded-lg border border-sky-100/30 text-sky-100/90',
    title: 'font-serif text-xl font-bold tracking-wide text-sky-50',
    tagline: 'font-semibold text-sky-100/90',
    badge: 'rounded-full bg-sky-200 px-2.5 text-sky-950 ring-2 ring-white/60',
    bullet: 'text-sky-100/85',
    bulletIcon: 'text-sky-200/60',
  },
  'royal-houses': {
    Icon: Sparkles,
    BulletIcon: Heart,
    image: `${ART}/realm-dawn-640.webp`,
    imageClass: 'saturate-[0.95] brightness-90',
    card: 'bg-violet-950 rounded-xl border border-violet-100/25',
    overlay: 'bg-gradient-to-b from-violet-950/45 via-slate-950/70 to-black/90',
    iconWrap: 'rounded-lg border border-violet-100/30 text-violet-100/90',
    title: 'font-serif text-xl font-bold tracking-wide text-violet-50',
    tagline: 'font-semibold text-violet-100/90',
    badge: 'rounded-full bg-violet-200 px-2.5 text-violet-950 ring-2 ring-white/60',
    bullet: 'text-violet-100/85',
    bulletIcon: 'text-violet-200/60',
  },
};

interface Props {
  readonly id: PresetDefId;
  readonly selected: boolean;
  /** The tab stop of the radiogroup (selected card, or the first when none is). */
  readonly focusable: boolean;
  readonly onSelect: (id: PresetDefId) => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export function PresetCard({ id, selected, focusable, onSelect, onKeyDown }: Props) {
  const t = useT();
  const look = PRESET_LOOK[id];
  const preset = presetById(id);
  const name = presetName(t, id);
  const testid = `setup-preset-${id}`;
  const bullets = [
    t(`setup.preset.${id}.b1`),
    t(`setup.preset.${id}.b2`),
    t(`setup.preset.${id}.b3`),
  ];
  // A preset may carry a fourth and fifth bullet; `t` falls back to the key
  // itself, so an absent one simply doesn't render.
  const b4Key = `setup.preset.${id}.b4`;
  const b4 = t(b4Key as MessageKey);
  if (b4 !== b4Key) bullets.push(b4);
  const b5Key = `setup.preset.${id}.b5`;
  const b5 = t(b5Key as MessageKey);
  if (b5 !== b5Key) bullets.push(b5);
  const { Icon, BulletIcon } = look;
  return (
    <button
      type="button"
      // biome-ignore lint/a11y/useSemanticElements: a rich card; radio semantics via role + aria-checked
      role="radio"
      aria-checked={selected}
      aria-labelledby={`${testid}-name`}
      aria-describedby={`${testid}-details`}
      tabIndex={focusable ? 0 : -1}
      onClick={() => onSelect(id)}
      onKeyDown={onKeyDown}
      data-testid={testid}
      {...appDom({ id: testid, type: 'Card', role: 'card', scope: 'main', label: name })}
      className={cn(
        'group relative isolate flex min-h-44 w-full cursor-pointer flex-col overflow-hidden text-left shadow-lg transition-all duration-200 lg:min-h-72',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/70',
        look.card,
        selected
          ? 'ring-4 ring-primary ring-offset-2 ring-offset-background'
          : 'opacity-95 hover:opacity-100',
      )}
    >
      <img
        src={look.image}
        alt=""
        aria-hidden
        loading="lazy"
        className={cn(
          'absolute inset-0 -z-20 size-full object-cover transition-transform duration-500 group-hover:scale-105',
          look.imageClass,
        )}
      />
      <span aria-hidden className={cn('absolute inset-0 -z-10', look.overlay)} />

      <span className="flex items-start justify-between gap-2 p-3 pb-1">
        <span className={cn('inline-flex size-10 items-center justify-center', look.iconWrap)}>
          <Icon className="size-5" aria-hidden />
        </span>
        <span className="flex items-center gap-1.5">
          {selected ? (
            <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md ring-2 ring-white/80">
              <Check className="size-4" strokeWidth={3} aria-hidden />
              <span className="sr-only">{t('setup.selected')}</span>
            </span>
          ) : null}
          <span
            className={cn('py-0.5 text-sm font-bold tabular-nums', look.badge)}
            title={t('setup.ageLabel', { age: preset.minAge })}
          >
            <span aria-hidden>{t('setup.ageBadge', { age: preset.minAge })}</span>
            <span className="sr-only">{t('setup.ageLabel', { age: preset.minAge })}</span>
          </span>
        </span>
      </span>

      <span className="mt-auto flex flex-col gap-1 p-3 pt-2">
        <span id={`${testid}-name`} className={cn('flex items-center gap-1.5', look.title)}>
          {name}
          {id === 'explorer' ? <Sparkles className="size-4 text-amber-200" aria-hidden /> : null}
        </span>
        <span id={`${testid}-details`} className="flex flex-col gap-1">
          <span className={cn('text-xs', look.tagline)}>{t(`setup.preset.${id}.tagline`)}</span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-white/85">
            <Clock className="size-3.5" aria-hidden />
            {formatSession(t, preset.sessionMin)}
          </span>
          <span className="mt-1 flex flex-col gap-1">
            {bullets.map((b) => (
              <span
                key={b}
                className={cn('flex items-start gap-1.5 text-[13px] leading-snug', look.bullet)}
              >
                <BulletIcon
                  className={cn('mt-0.5 size-3.5 shrink-0', look.bulletIcon)}
                  aria-hidden
                />
                <span>{b}</span>
              </span>
            ))}
          </span>
        </span>
      </span>
    </button>
  );
}
